import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Hooks
import { useAppDispatch } from '../../../../hooks';
import useAppSelector from '../../../../hooks/useAppSelector';

// Constants
import endpoints from '../../../../constants/configs/endpoints';
import STATES from '../../../../constants/configs/states';
import { SPINNER_MESSAGES_IDS } from '../../../../constants/common/miscellaneous';
import { FROM_TAB, PLAN_TYPE } from '../../../../modules/custom-grouping/modals/modalTypes';

// Actions
import { startSpinner, stopSpinner } from '../../../../actions/common/actionCreators';

// Selectors
import { environmentSelector } from '../../../../selectors/commonSelectors';

// API
import { api } from '../../../../modules/common-utils-services/axiosUtil';

// Types
import {
    ICheckAptcRequest,
    ICheckForPendingAppRequest,
    IPayForPlanRequest,
    IPlan,
    ISaveAptcRequest,
} from '../planSummary.types';

// Modals
import { PlanSummaryModals } from '../modals/planSummaryModals';
import { getPlanSummaryData } from '../../../../actions/individualPortal/planSummary/planSummaryActionCreators';

const SHOW_APTC_INFO_TABULAR = [STATES.MINNESOTA];
const HIDE_PAYMENT_LINK = [STATES.MINNESOTA];
const HAS_ACCORDION_PANEL_LEFT_RIGHT_PADDING = [STATES.MINNESOTA];

const usePlanSummary = () => {
    const {
        stateCode,
    } = useAppSelector(environmentSelector);
    const dispatch = useAppDispatch();
    const { state } = useLocation();

    const isAptcSectionTableView = useMemo(() => SHOW_APTC_INFO_TABULAR.includes(stateCode), [stateCode]);
    const isPaymentLinkHidden = useMemo(() => HIDE_PAYMENT_LINK.includes(stateCode), [stateCode]);
    const hasAccordionPanelFixedPadding = useMemo(() => !HAS_ACCORDION_PANEL_LEFT_RIGHT_PADDING.includes(stateCode), [stateCode]);
    const aptcModalHeight = stateCode === STATES.MINNESOTA ? '350px' : '400px';
    const aptcHeadingFontSize = stateCode === STATES.MINNESOTA ? '14px' : '16px';

    const fetchPlanSummaryData = () => {
        const formData = new FormData();
        const caseNumber = state?.caseNumber ?? '';
        formData.append('caseNumber', caseNumber);
        dispatch(getPlanSummaryData({ payload: formData }));
    };

    const payForPlan = async (params: IPayForPlanRequest, openModal: Function) => {
        const url = endpoints.PAY_FOR_HEALTH;
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const response = await api.post(url, params);
            if (response.data) {
                openModal('payForEnrollment', { finEnrlHltDataKey: response.data });
            } else {
                openModal('disenrollFailureModal');
            }
        } catch (e) {
            openModal('disenrollFailureModal');
        } finally {
            dispatch(stopSpinner());
        }
    };

    const checkAptc = async (params: ICheckAptcRequest, plan: IPlan, openModal) => {
        const url = endpoints.PLAN_SUMMARY_CHECK_APTC;
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const response = await api.post(url, params);

            if (response.data) {
                const { aptcAmt: maxAptc, aptcPremiumEffectiveDate: premiumEffDate } = response.data;

                openModal(PlanSummaryModals.APTCSuccess, {
                    plan,
                    premiumEffDate,
                });
            } else {
                openModal(PlanSummaryModals.APTCFailure);
            }
        } catch (e) {
            openModal(PlanSummaryModals.APTCFailure);
        } finally {
            dispatch(stopSpinner());
        }
    };

    const saveAptc = async (params: ISaveAptcRequest) => {
        const url = endpoints.PLAN_SUMMARY_UPDATE_APTC;
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const response = await api.post(url, params);
            return response.data?.success || false;
        } catch (e) {
            return false;
        } finally {
            dispatch(stopSpinner());
        }
    };

    const checkForPendingApplication = async ({ healthPlan, dentalPlan, openModal, action }: ICheckForPendingAppRequest) => {
        const url = endpoints.CHECK_FOR_PENDING_APPLICATION;
        const plan = healthPlan ?? dentalPlan!;
        const coverageYear = plan.coverageStartDate.split('/')[2];
        const params = { coverageYear };
        const planType = healthPlan && dentalPlan ? PLAN_TYPE.BOTH : healthPlan ? PLAN_TYPE.HEALTH : PLAN_TYPE.DENTAL;
        const dentalDetails = planType === PLAN_TYPE.BOTH ? dentalPlan : undefined;
        try {
            dispatch(startSpinner(SPINNER_MESSAGES_IDS.LOADING_DATA));
            const res = await api.post(url, params);
            if (res?.data && res.data.message.toUpperCase() === 'TRUE') {
                openModal('pendingAppModal');
            } else {
                openModal('disenrollOptions', { enrollmentAction: action, typeOfPlan: planType, group: plan, dentalDetails, fromTab: FROM_TAB.PLANSUMMARY });
            }
        } catch (e) {
            openModal('disenrollFailureModal');
        } finally {
            dispatch(stopSpinner());
        }
    };

    return {
        hasAccordionPanelFixedPadding,
        isAptcSectionTableView,
        isPaymentLinkHidden,
        payForPlan,
        checkAptc,
        saveAptc,
        checkForPendingApplication,
        aptcModalHeight,
        aptcHeadingFontSize,
        fetchPlanSummaryData,
    };
};

export default usePlanSummary;
